import './leadsKanbanV2.scss'
import _ from 'lodash'
import leadsService from 'services/admin/leadsService';
import { useGetLeads } from 'hooks/useGetLeads';
import { Button, Card, CardContent, Dialog, DialogContent, DialogTitle, FormControl, IconButton, InputLabel, MenuItem, Select, Tooltip } from '@mui/material';
import { useEffect, useState } from 'react';
import { Lead } from 'services/models/Lead';
import { toast } from 'react-toastify';
import { queryClient } from 'queryClient';
import { Controller, useForm } from 'react-hook-form';
import pipelinesAdminService from 'services/admin/pipelinesAdminService';
import { AddPipelineForm } from './components/AddPipelineForm';
import { useQuery } from '@tanstack/react-query';
import LeadFormWrapper from './LeadForm';
import { FaCalendarAlt, FaInfoCircle, FaPlus, FaUser, FaUsers, FaWallet } from 'react-icons/fa';
import { toDate } from 'helpers/dateHelper';
import { toCurrencyFormat } from 'helpers/utils';


const getDefaultPipelineID = (pipelines: any[]) => {
    const storedPipelineID = localStorage.getItem('selectedPipelineID') ? Number(localStorage.getItem('selectedPipelineID')) : null
    if (storedPipelineID) {
        const pipeline = pipelines.find((p: any) => p.id === storedPipelineID)
        if (pipeline) {
            return pipeline.id
        }
    }
    return _.get(pipelines, '[0].id')
}

const LeadsKanbanV2 = ({
    pipelines,
}: any) => {
    const [dragOverStageType, setDragOverStageType] = useState<string | null>(null)
    const [draggedLeadID, setDraggedLeadID] = useState<number | null>(null)

    const queryFormMethods = useForm({
        defaultValues: {
            pipelineID: getDefaultPipelineID(pipelines),
            status: 'NEW',
        },
        mode: 'onChange',
    })

    const pipelineID = queryFormMethods.watch('pipelineID')
    const pipeline = pipelines?.find((p: any) => p.id === pipelineID)

    const { data: leads, refetch: refetchLeads } = useGetLeads({
        pipelineID,
        status: queryFormMethods.watch('status'),
    })

    // Dialogs
    const [isOpenLeadDetail, setIsOpenLeadDetail] = useState(false)
    const [isOpenAddPipeline, setIsOpenAddPipeline] = useState(false)
    const [isSelectPipelineOpen, setIsSelectPipelineOpen] = useState(false)
    const [lead, setLead] = useState<Lead | null>(null)

    useEffect(() => {
        localStorage.setItem('selectedPipelineID', pipelineID)
    }, [pipelineID])


    const createOrUpdateLead = (updatedLeadOrNewLead: { isNew: boolean, lead: Lead }) => {
        console.log("updatedLead:", updatedLeadOrNewLead)
        setIsOpenLeadDetail(false)
        setLead(null)
        refetchLeads()
    }

    const handleAddPipelineSubmit = async (formData: any) => {
        console.log("add pipeline", formData)
        try {
            const { data: { pipeline } } = await pipelinesAdminService.post(formData)
            console.log("pipeline:", pipeline)
            toast.success("Воронка добавлена успешно!")

            // Добавляем воронку в кеш
            queryClient.setQueryData(['pipelines'], (old: any) => {
                return [...old, pipeline]
            })
        } catch (error) {
            toast.error("Ошибка при добавлении воронки!")
            console.error(error)
        } finally {
            setIsOpenAddPipeline(false)
        }
    }


    const openLeadDetailsDialog = () => {
        setIsOpenLeadDetail(true)
        setLead(null)
    }

    const handleDragStart = (e: React.DragEvent<HTMLDivElement>, lead: Lead) => {
        setDraggedLeadID(lead.id)
        e.dataTransfer.effectAllowed = 'move'
        e.dataTransfer.setData('application/json', JSON.stringify({ leadID: lead.id, currentStage: lead.stepID }))
        e.dataTransfer.setData('text/plain', lead.id.toString())
    }

    const handleDragEnd = () => {
        setDraggedLeadID(null)
        setDragOverStageType(null)
    }

    const handleDragOver = (e: React.DragEvent<HTMLDivElement>, stageType: string) => {
        e.preventDefault()
        e.dataTransfer.dropEffect = 'move'
        setDragOverStageType(stageType)
    }

    const handleDragEnter = (e: React.DragEvent<HTMLDivElement>, stageType: string) => {
        e.preventDefault()
        setDragOverStageType(stageType)
    }

    const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node)) {
            setDragOverStageType(null)
        }
    }

    const handleDrop = async (e: React.DragEvent<HTMLDivElement>, stageType: string) => {
        e.preventDefault()
        setDragOverStageType(null)
        const leadID = e.dataTransfer.getData('text/plain')
        const lead = leads?.rows?.find((l: Lead) => l.id === Number(leadID))
        if (lead) {
            await leadsService.patch(lead.id, { stepID: stageType })

            queryClient.setQueriesData({ queryKey: ['leads', { pipelineID }] }, (old: any) => {
                return {
                    ...old,
                    rows: old.rows.map((l: Lead) => l.id === lead.id ? { ...l, stepID: stageType } : l)
                }
            })
        }
    }

    return <div className='leads-kanban'>
        <div className='flex-row flex-space-between items-center'>
            <div className='flex gap-4 items-center'>
                <h3 className="mb-4">
                    Сделки
                    <Tooltip title="Сделки — Это объект процесса. Она отвечает на вопрос: «Что мы продаем и на какой стадии находится продажа?»">
                        <IconButton>
                            <FaInfoCircle size={14} />
                        </IconButton>
                    </Tooltip>
                </h3>
                {/* Pipelines(Воронки) */}
                <Controller control={queryFormMethods.control} name="pipelineID" render={({ field }) => {
                    return <FormControl>
                        <InputLabel>Воронка</InputLabel>
                        <Select
                            label="Воронка"
                            style={{
                                width: 200,
                                maxWidth: 200,
                            }}
                            open={isSelectPipelineOpen}
                            size="small"
                            onOpen={() => setIsSelectPipelineOpen(true)}
                            onClose={() => setIsSelectPipelineOpen(false)}
                            {...field}>
                            {pipelines?.map((pipeline: any) => (
                                <MenuItem key={pipeline.id} value={pipeline.id}>{pipeline.name}</MenuItem>
                            ))}
                            <hr className='my-2' />
                            <MenuItem onMouseDown={(e) => {
                                e.preventDefault()
                                e.stopPropagation()
                                setIsOpenAddPipeline(true)
                                setIsSelectPipelineOpen(false)
                            }}>+ Добавить воронку</MenuItem>
                        </Select>
                    </FormControl>
                }} />
                {/* Статусы сделок */}
                <Controller control={queryFormMethods.control} name="status" render={({ field }) => {
                    return <FormControl>
                        <InputLabel>Статус</InputLabel>
                        <Select label="Статус" size="small" {...field}>
                            <MenuItem value="NEW">Активные</MenuItem>
                            <MenuItem value="ARCHIVED">Архивные</MenuItem>
                        </Select>
                    </FormControl>
                }} />
            </div>
            <Button size="small" variant="contained" color="primary" onClick={openLeadDetailsDialog}>
                <FaPlus size={12} className="mr-2" />
                Создать
            </Button>
        </div>
        <div className='board-container'>
            {pipeline?.steps?.map((step: any) => (
                <div key={step.id} className="column">
                    <div className="column-header">
                        <span className='text-md'>{step.name}</span>
                        <span className='text-sm'>({leads?.rows?.filter((l: Lead) => l.stepID === step.id)?.length || 0})</span>
                    </div>
                    {/* Общая сумма сделок в этой стадии */}
                    <div className='flex items-center gap-1 text-center w-100 justify-center py-1'>
                        <span className='text-md font-bold'>{toCurrencyFormat(leads?.rows?.filter((l: Lead) => l.stepID === step.id)?.reduce((acc: number, lead: Lead) => acc + (Number(lead.budget) || 0), 0) || 0)}</span>
                    </div>
                    <div className={`column-content ${dragOverStageType === step.id ? 'drag-over' : ''}`}
                        onDragOver={(e) => handleDragOver(e, step.id)}
                        onDragEnter={(e) => handleDragEnter(e, step.id)}
                        onDragLeave={handleDragLeave}
                        onDrop={(e) => handleDrop(e, step.id)}
                    >
                        {leads?.rows?.filter((l: Lead) => l.stepID === step.id)?.map((lead: Lead) => (
                            <div className={`card-wrapper ${draggedLeadID === lead.id ? 'dragging' : ''}`}
                                key={lead.id}
                                draggable={true}
                                onDragStart={(e) => handleDragStart(e, lead)}
                                onDragEnd={handleDragEnd}
                            >
                                <Card>
                                    <CardContent>
                                        <div onClick={() => {
                                            setLead(lead)
                                            setIsOpenLeadDetail(true)
                                        }}>
                                            <div className='card-title-item flex items-center gap-1'>
                                                <span className='card-title-item-value text-sm font-bold color-blue'>{lead.name} #{lead.id}</span>
                                            </div>
                                            <div className='card-title-item flex items-center gap-1'>
                                                <FaCalendarAlt size={12} />
                                                <span className='card-title-item-value text-sm'>{toDate(lead.createdAt)}</span>
                                            </div>
                                            <div className='card-title-item flex items-center gap-1'>
                                                <FaWallet size={12} />
                                                <span className='card-title-item-value text-sm'>{lead.budget ? toCurrencyFormat(lead.budget, lead.currency) : '-'}</span>
                                            </div>
                                            {/* <div className='card-title-item flex items-center gap-1'>
                                                <FaUser size={12} />
                                                <span className='card-title-item-value text-sm'>{lead.manager?.name || '-'}</span>
                                            </div> */}
                                            <div className='card-title-item flex items-center gap-1'>
                                                <FaUser size={12} />
                                                <span className='card-title-item-value text-sm'>{lead.customers?.map((customer: any) => customer.firstName).join(', ') || '-'}</span>
                                            </div>
                                        </div>
                                    </CardContent>
                                </Card>
                            </div>
                        ))}
                    </div>
                </div>
            ))}
        </div>
        {/* Lead form */}
        <Dialog fullWidth maxWidth="xl" open={isOpenLeadDetail} onClose={() => { setIsOpenLeadDetail(false) }} sx={{
            '& .MuiDialog-paper': {
                height: '90vh',
            },
        }}>
            <DialogContent>
                <LeadFormWrapper
                    leadID={lead?.id}
                    onClose={createOrUpdateLead}
                    selectedPipelineID={pipelineID}
                />
            </DialogContent>
        </Dialog>
        {/* Add pipeline dialog */}
        <Dialog fullWidth maxWidth="sm" open={isOpenAddPipeline} onClose={() => { setIsOpenAddPipeline(false) }}>
            <DialogTitle>Добавить воронку</DialogTitle>
            <DialogContent>
                <AddPipelineForm onSubmit={handleAddPipelineSubmit} />
            </DialogContent>
        </Dialog>
    </div>
}

export default () => {
    const { data: pipelines, isLoading } = useQuery({
        queryKey: ['pipelines', 'leads'],
        queryFn: async () => {
            const response: any = await pipelinesAdminService.getAll({
                type: 'leads',
            })
            return response.data
        },
        retry: false,
    })

    if (isLoading) return <div>Loading...</div>

    return <LeadsKanbanV2 pipelines={pipelines} />
}
